// "Submit to queue" — one button, mounted in the ledger's claim detail and in the
// claim editor's action row.
//
// It gates itself, so each mount is one line: nothing renders unless the practice
// has `features.submit_queue` (a Stedi practice submits natively and never sees
// it) and the claim is in a status A's enqueue accepts. The server is still the
// authority on both; this only decides whether the button is offered.
//
// What a click can come back with:
//   201 — the row is queued: the button becomes the row's age chip, plus any rules
//         WARNs the biller should read (they did not block the enqueue).
//   422 — the claim is not clean: the rules reasons inline, with a way into the
//         editor to fix them.
//   409 — with `item_id`: a live row already exists, "already queued". Without it
//         (the claim's status moved on since the ledger loaded): the server's message.
// Anything else is an error message, never a silent no-op.

import React, { useEffect, useState } from "react";
import useCapabilities from "../../hooks/useCapabilities";
// Straight from the module, not "../claims": the claim editor mounts this button,
// and going through that index would make the two features import each other.
import { openClaimEditor } from "../claims/claimEditorTarget";
import {
  ENQUEUEABLE_STATUSES,
  enqueueClaim,
  fetchClaimLiveItem,
  isAlreadyQueued,
  itemFrom,
  portalLabel,
  reasonsFrom,
  warningsFrom,
} from "./api";
import { ageLabel, ageTone } from "./ageing";
import usePmsGates from "./usePmsGates";
import "./Queue.css";

export default function SubmitToQueueButton({
  practiceId,
  claim,
  onPageChange,
  readOnly = false,
  dirty = false,
  // Set by the claim editor when its "ready to submit" checklist has a red row
  // (claim_readiness_service — the same check enqueue refuses on). The button stays
  // visible and says why it is disabled, rather than letting a click earn a 422.
  blockedReason = null,
  // Show the practice's portal beside the button, not only in its tooltip. The claim
  // editor sets it: there is one claim on screen and room to say where it is going.
  // OFF in the ledger, where this button is mounted on every row and fifty copies of
  // "→ Practice Fusion" would be noise. The tooltip carries it either way.
  showDestination = false,
}) {
  const { features } = useCapabilities(practiceId);
  // Feature gating by data (brief 15): for a practice whose claims leave through a
  // portal runbook, the button is offered only when the runbook covers `submit`'s
  // four operations. Open for every other practice, and while the gate loads.
  const { gates } = usePmsGates(practiceId);
  const [busy, setBusy] = useState(false);
  // {kind: 'queued'|'already'|'refused'|'error', item?, reasons?, warnings?, message?}
  const [outcome, setOutcome] = useState(null);
  // The claim's live queue row, if it has one, so a claim queued earlier (or by
  // someone else) shows its chip instead of offering the button again.
  const [liveItem, setLiveItem] = useState(null);
  // Which portal this practice drains to, so the control can say where the claim is
  // going. It arrives on the same response as `liveItem` — see `fetchClaimLiveItem`.
  // null means the practice has not named one, and the control says nothing extra.
  const [portal, setPortal] = useState(null);
  const enabled =
    !!practiceId && !!claim && !readOnly && !!features.submit_queue;
  const claimId = claim?.id;

  useEffect(() => {
    if (!enabled || claimId == null) return undefined;
    let cancelled = false;
    fetchClaimLiveItem(practiceId, claimId)
      .then(({ item, portal: key }) => {
        if (cancelled) return;
        setLiveItem(item);
        setPortal(key);
      })
      .catch(() => {
        // Falls back to offering the button; the server's 409 still says
        // "already queued". The Queue page shows its own load errors.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, practiceId, claimId]);

  const where = portalLabel(portal);
  const destination = where ? ` for ${where}` : "";
  const gate = gates.submit;
  const gateReason = gate.allowed ? null : gate.reason;

  if (!practiceId || !claim || readOnly) return null;
  if (!features.submit_queue) return null;
  // A queued row stays visible after its claim changes status, so the chip wins
  // over the status check.
  if (
    !outcome &&
    !liveItem &&
    !ENQUEUEABLE_STATUSES.includes(claim.claim_status)
  )
    return null;

  const enqueue = async (e) => {
    if (e) e.stopPropagation();
    if (busy || dirty || blockedReason || gateReason) return;
    setBusy(true);
    setOutcome(null);
    try {
      const body = await enqueueClaim(practiceId, claim.id);
      setOutcome({
        kind: "queued",
        item: itemFrom(body),
        warnings: warningsFrom(body),
      });
    } catch (err) {
      if (err.status === 422) {
        setOutcome({
          kind: "refused",
          reasons: reasonsFrom(err.body),
          message: err.message,
          // {error, codes}: the 837 builder refused the claim, not the rules. The
          // message says so; the codes are the editor's own list.
          notSubmittable: !!(err.body && Array.isArray(err.body.codes)),
        });
      } else if (err.status === 409 && isAlreadyQueued(err.body)) {
        setOutcome({ kind: "already", item: null });
      } else {
        setOutcome({ kind: "error", message: err.message });
      }
    } finally {
      setBusy(false);
    }
  };

  if (!outcome && liveItem) {
    const held = liveItem.status === "held";
    return (
      <div className="sq-inline" onClick={(e) => e.stopPropagation()}>
        <span
          className={`sq-age-chip ${held ? "red" : ageTone(liveItem.ageDays)}`}
          title={
            held
              ? "Submit was pressed and no reference was captured — resolve it on the Submit queue page"
              : `On the submit queue for this practice${destination}`
          }
        >
          {held
            ? "Held in queue"
            : `${liveItem.status === "leased" ? "Being worked" : "Queued"} · ${ageLabel(liveItem.ageDays)}`}
        </span>
      </div>
    );
  }

  if (outcome && (outcome.kind === "queued" || outcome.kind === "already")) {
    const days = outcome.item ? outcome.item.ageDays : 0;
    const tone = ageTone(days);
    return (
      <div className="sq-inline" onClick={(e) => e.stopPropagation()}>
        <span
          className={`sq-age-chip ${tone}`}
          title={`On the submit queue for this practice${destination}`}
        >
          {outcome.kind === "already" ? "Already queued" : "Queued"}
          {outcome.item ? ` · ${ageLabel(days)}` : ""}
        </span>
        {outcome.warnings && outcome.warnings.length > 0 && (
          <div className="sq-popover sq-warn" role="status">
            <button
              className="sq-popover-close"
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                setOutcome({ ...outcome, warnings: [] });
              }}
            >
              ×
            </button>
            <strong>
              Queued with {outcome.warnings.length} warning
              {outcome.warnings.length === 1 ? "" : "s"}
            </strong>
            <ul>
              {outcome.warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="sq-inline" onClick={(e) => e.stopPropagation()}>
      <button
        className="btn btn-secondary btn-sm"
        onClick={enqueue}
        disabled={busy || dirty || !!blockedReason || !!gateReason}
        data-gated={gateReason ? "yes" : undefined}
        title={
          dirty
            ? "Save your edits first — the queue sends the saved claim"
            : blockedReason ||
              gateReason ||
              `Put this claim on the practice's submit queue${destination}`
        }
      >
        {busy ? "Queueing…" : "Submit to queue"}
      </button>
      {/* Where it is going, once the practice has said. A biller working two
          practices should not have to remember which portal this one drains to. */}
      {showDestination && where && !blockedReason && !gateReason && (
        <span className="sq-muted sq-portal-hint">&rarr; {where}</span>
      )}
      {/* The gate's reason beside the button where there is room for it (the claim
          editor); in the ledger the tooltip carries it. */}
      {showDestination && gateReason && (
        <span className="sq-muted sq-portal-hint" data-testid="submit-gate">
          {gateReason}{" "}
          {onPageChange && (
            <button
              className="sq-link"
              onClick={(e) => {
                e.stopPropagation();
                onPageChange("pms-coverage");
              }}
            >
              Coverage matrix
            </button>
          )}
        </span>
      )}
      {outcome && outcome.kind === "refused" && (
        <div className="sq-popover sq-refused" role="alert">
          <button
            className="sq-popover-close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              setOutcome(null);
            }}
          >
            ×
          </button>
          <strong>Not queued — this claim is not clean.</strong>
          {outcome.notSubmittable && outcome.reasons.length > 0 && (
            <p>{outcome.message}</p>
          )}
          {outcome.reasons.length > 0 ? (
            <ul>
              {outcome.reasons.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p>{outcome.message}</p>
          )}
          {onPageChange && features.claim_editor && (
            <button
              className="sq-link"
              onClick={(e) => {
                e.stopPropagation();
                openClaimEditor(claim.id, onPageChange, practiceId);
              }}
            >
              Fix it in the claim editor
            </button>
          )}
        </div>
      )}
      {outcome && outcome.kind === "error" && (
        <div className="sq-popover sq-error" role="alert">
          <button
            className="sq-popover-close"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              setOutcome(null);
            }}
          >
            ×
          </button>
          {outcome.message}
        </div>
      )}
    </div>
  );
}
